/**
 * Agent Providers Service
 *
 * Single source of truth for agent provider configuration. Configuration is loaded
 * via getAgentProviders() so it can be replaced with an API call later without
 * changing consuming code.
 *
 * In the Outform Fields SDUI schema:
 * - provider.id is the UUID (used as form targetSystem value)
 * - provider.type is the backend connection type
 * - provider.authSchemeFormFields replaces authMethods
 * - auth scheme's authScheme string is used as the form authMethod value
 * - field.id maps 1:1 to backend API payload keys (no formFieldKey indirection)
 */

// Configuration is loaded from the getTargetSystems API endpoint.
import { getTargetSystems } from '~/services/agentScannerService';
import {
  PROVIDER_LABELS,
  ASSET_TYPE_LABELS,
  FORM_ASSET_TYPE_LABELS
} from '../../../types/commonTypes';

let injectedConfig;
let cachedConfig;

/**
 * Internal synchronous provider resolver.
 * Returns providers from injected config or cached config.
 * Returns an empty array before the API has resolved.
 */
function resolveProvidersSync() {
  if (injectedConfig !== undefined) {
    return injectedConfig?.providers || [];
  }

  if (cachedConfig) {
    return cachedConfig.providers || [];
  }

  return [];
}

/**
 * Returns agent providers configuration from the API.
 * Caches the result after the first successful call.
 * @param {Object} [context] - Request context with authorization
 * @param {string} [organizationId] - Organization ID
 * @returns {Promise<{ schemaVersion: string, providers: Array }>} Agent providers config
 */
export async function getAgentProviders(context, organizationId) {
  if (injectedConfig !== undefined) {
    return injectedConfig;
  }

  if (cachedConfig) {
    return cachedConfig;
  }

  if (!context) {
    return { schemaVersion: '1.0', providers: [] };
  }

  try {
    const apiResponse = await getTargetSystems({ organizationId, context });

    const hasSchemaFields =
      Array.isArray(apiResponse) &&
      apiResponse.length > 0 &&
      apiResponse.some((p) => Array.isArray(p?.authSchemeFormFields));

    const providers = hasSchemaFields ? apiResponse : [];

    cachedConfig = {
      schemaVersion: '1.0',
      providers
    };

    return cachedConfig;
  } catch (error) {
    return { schemaVersion: '1.0', providers: [] };
  }
}

/**
 * Allow tests or future API to set config without API call (e.g. inject mock).
 * Pass undefined to reset to API source.
 * @param {{ schemaVersion?: string, providers?: Array } | undefined} config
 */
export function setAgentProvidersConfig(config) {
  injectedConfig = config;
}

/**
 * Reset the cached API response.
 * Useful for tests or when context changes.
 */
export function resetAgentProvidersCache() {
  cachedConfig = undefined;
}

/**
 * In the SDUI schema the provider UUID is used directly as the form
 * targetSystem value.  These helpers are kept as thin identity wrappers
 * so callers that relied on the old indirection layer keep compiling.
 */

/**
 * @param {string} providerId - Provider UUID from JSON
 * @returns {string} Form targetSystem value (same as provider UUID)
 */
export function getFormTargetSystemFromProviderId(providerId) {
  return providerId;
}

/**
 * @param {string} targetSystem - Form targetSystem value (provider UUID)
 * @param {Object} [context] - Request context with authorization
 * @returns {string | undefined} Provider UUID (same as input if found)
 */
export function getProviderIdFromFormTarget(targetSystem) {
  const providers = resolveProvidersSync();
  const provider = providers.find((p) => p.id === targetSystem);

  return provider ? provider.id : undefined;
}

/**
 * In SDUI the authScheme string is used as the form authMethod value.
 * @param {string} authScheme - Auth scheme from JSON (e.g. 'ApiKey')
 * @returns {string} Form authMethod value (same as authScheme)
 */
export function getFormAuthMethodFromAuthMethodId(authScheme) {
  return authScheme;
}

/**
 * @param {string} authMethod - Form authMethod value (authScheme string)
 * @returns {string} Auth scheme from JSON (same as input)
 */
export function getAuthMethodIdFromFormAuth(authMethod) {
  return authMethod;
}

function mapProviderOptions(providers) {
  return providers.map((p) => ({
    label: p.name,
    value: p.id,
    disabled: false
  }));
}

function mapAuthMethodOptions(providers, targetSystem) {
  if (!targetSystem) {
    const seen = new Set();

    return providers
      .flatMap((p) => p.authSchemeFormFields || [])
      .filter((m) => {
        const v = m.authScheme;

        if (seen.has(v)) return false;
        seen.add(v);

        return true;
      })
      .map((m) => ({
        label: m.name,
        value: m.authScheme
      }));
  }

  const provider = providers.find((p) => p.id === targetSystem);

  if (!provider || !Array.isArray(provider.authSchemeFormFields)) return [];

  return provider.authSchemeFormFields.map((m) => ({
    label: m.name,
    value: m.authScheme
  }));
}

/**
 * @param {Object} [context] - Request context with authorization
 * @param {string} [organizationId] - Organization ID
 * @returns {Array<{ label: string, value: string, disabled?: boolean }>}
 */
export async function getProviderOptions(context, organizationId) {
  const config = await getAgentProviders(context, organizationId);

  return mapProviderOptions(config?.providers || []);
}

/**
 * Synchronous version of getProviderOptions using resolved providers.
 * @returns {Array<{ label: string, value: string, disabled?: boolean }>}
 */
export function getProviderOptionsSync() {
  return mapProviderOptions(resolveProvidersSync());
}

/**
 * @param {string} targetSystem - Form targetSystem value / provider UUID (empty = show all)
 * @param {Object} [context] - Request context with authorization
 * @param {string} [organizationId] - Organization ID
 * @returns {Array<{ label: string, value: string }>}
 */
export async function getAuthMethodOptionsForTarget(
  targetSystem,
  context,
  organizationId
) {
  const config = await getAgentProviders(context, organizationId);

  return mapAuthMethodOptions(config?.providers || [], targetSystem);
}

/**
 * Synchronous version of getAuthMethodOptionsForTarget using resolved providers.
 * @param {string} targetSystem - Form targetSystem value / provider UUID
 * @returns {Array<{ label: string, value: string }>}
 */
export function getAuthMethodOptionsForTargetSync(targetSystem) {
  return mapAuthMethodOptions(resolveProvidersSync(), targetSystem);
}

/**
 * Get scenario configuration for payload building and field rendering.
 * In the SDUI schema field.id maps 1:1 to backend payload keys, so
 * fieldMapping is an identity map (key === value).
 *
 * @param {string} targetSystem - Form targetSystem value (provider UUID)
 * @param {string} authMethod - Form authMethod value (authScheme string)
 * @returns {{
 *   targetSystemId: string,
 *   backendType: string,
 *   authScheme: string,
 *   fieldMapping: Record<string, string>,
 *   fields: Array<{ id: string, type: string, label: string, placeholderIn?: object, requiredIn?: object, options?: Array, defaultValue?: string }>,
 *   sensitiveFormFieldNames: string[]
 * } | null}
 */
export function getScenarioConfigFromProviders(targetSystem, authMethod) {
  if (!targetSystem || !authMethod) return null;

  const providers = resolveProvidersSync();
  const provider = providers.find((p) => p.id === targetSystem);

  if (!provider) return null;

  const authSchemeConfig = provider.authSchemeFormFields?.find(
    (m) => m.authScheme === authMethod
  );

  if (!authSchemeConfig || !Array.isArray(authSchemeConfig.fields)) return null;

  const { fields } = authSchemeConfig;
  const fieldMapping = {};
  const sensitiveFormFieldNames = [];

  fields.forEach((f) => {
    fieldMapping[f.id] = f.id;
    if (f.type === 'password') {
      sensitiveFormFieldNames.push(f.id);
    }
  });

  return {
    targetSystemId: provider.id,
    backendType: provider.type,
    authScheme: authSchemeConfig.authScheme,
    fieldMapping,
    fields,
    sensitiveFormFieldNames
  };
}

/**
 * Field defaults for a scenario (field.id -> '' or defaultValue).
 * @param {string} targetSystem
 * @param {string} authMethod
 * @returns {Record<string, string>}
 */
export function getScenarioFieldDefaultsFromProviders(
  targetSystem,
  authMethod
) {
  const config = getScenarioConfigFromProviders(targetSystem, authMethod);

  if (!config) return {};

  return config.fields.reduce((acc, f) => {
    acc[f.id] = f.defaultValue ?? (f.type === 'chip-input' ? [] : '');

    return acc;
  }, {});
}

/**
 * List of form field names for the scenario (for connection field detection and clearing).
 * @param {string} targetSystem
 * @param {string} authMethod
 * @returns {string[]}
 */
export function getScenarioFormFieldNames(targetSystem, authMethod) {
  const config = getScenarioConfigFromProviders(targetSystem, authMethod);

  if (!config) return [];

  return config.fields.map((f) => f.id);
}

/**
 * Sensitive (password) form field names for the scenario.
 * @param {string} targetSystem
 * @param {string} authMethod
 * @returns {string[]}
 */
export function getSensitiveFormFieldsForScenario(targetSystem, authMethod) {
  const config = getScenarioConfigFromProviders(targetSystem, authMethod);

  return config ? config.sensitiveFormFieldNames : [];
}

/**
 * Get connection form field names for an auth method across all matching providers.
 * @param {string} authMethod - Form auth method value (authScheme string)
 * @returns {string[]}
 */
export function getConnectionFieldNamesForAuthMethod(authMethod) {
  const providers = resolveProvidersSync();
  const allFields = new Set();

  for (const p of providers) {
    const names = getScenarioFormFieldNames(p.id, authMethod);

    names.forEach((n) => allFields.add(n));
  }

  return [...allFields];
}

/**
 * Get sensitive form field names for an auth method across all matching providers.
 * @param {string} authMethod - Form auth method value (authScheme string)
 * @returns {string[]}
 */
export function getSensitiveFieldNamesForAuthMethod(authMethod) {
  const providers = resolveProvidersSync();
  const allFields = new Set();

  for (const p of providers) {
    const names = getSensitiveFormFieldsForScenario(p.id, authMethod);

    names.forEach((n) => allFields.add(n));
  }

  return [...allFields];
}

/**
 * Build backend auth params from form data using field.id as keys.
 * In SDUI, field.id maps 1:1 to backend keys, so no translation is needed.
 * Preserves null for unchanged sensitive fields in edit mode.
 *
 * @param {string} targetSystem
 * @param {string} authMethod
 * @param {Record<string, *>} formParams - Flattened form data keyed by field.id
 * @returns {Record<string, *>} Backend auth params
 */
export function transformAuthParamsFromProviders(
  targetSystem,
  authMethod,
  formParams
) {
  const config = getScenarioConfigFromProviders(targetSystem, authMethod);

  if (!config) return {};

  const result = {};

  config.fields.forEach((f) => {
    const value = formParams[f.id];

    result[f.id] = value !== undefined ? value : '';
  });

  return result;
}

/**
 * Map backend auth param names to frontend field names (for edit mode when loading scanner).
 * In SDUI field.id is 1:1 with backend, so this is an identity mapping.
 * @param {string} targetSystem
 * @param {string} authMethod
 * @returns {Record<string, string>} backendKey -> frontendKey (identity)
 */
export function getBackendToFrontendFieldMapping(targetSystem, authMethod) {
  const config = getScenarioConfigFromProviders(targetSystem, authMethod);

  if (!config) return {};

  const mapping = {};

  config.fields.forEach((f) => {
    mapping[f.id] = f.id;
  });

  return mapping;
}

/**
 * Resolve target system form value from API targetSystemId (UUID).
 * In SDUI the provider UUID *is* the targetSystemId, so this is a direct lookup.
 * @param {string} targetSystemId - UUID from API
 * @returns {string | undefined}
 */
export function getFormTargetSystemByTargetSystemId(targetSystemId) {
  const providers = resolveProvidersSync();
  const provider = providers.find((p) => p.id === targetSystemId);

  return provider ? provider.id : undefined;
}

/**
 * Resolve auth method form value from API authScheme.
 * In SDUI the authScheme string *is* the form value, so this is identity.
 * @param {string} authScheme - e.g. 'ApiKey'
 * @param {string} [targetSystemId] - Optional, unused in SDUI but kept for API compat
 * @returns {string}
 */
export function getFormAuthMethodByAuthScheme(authScheme) {
  return authScheme;
}

/**
 * Returns distinct provider options from the target systems list.
 * Uses PROVIDER_LABELS for display names.
 * @returns {Array<{ label: string, value: string }>}
 */
export function getDistinctProvidersSync() {
  const providers = resolveProvidersSync();
  const seen = new Set();
  const options = [];

  providers.forEach((p) => {
    const providerKey = (p.provider || '').toLowerCase();

    if (providerKey && !seen.has(providerKey)) {
      seen.add(providerKey);
      options.push({
        label: PROVIDER_LABELS[providerKey] || providerKey,
        value: providerKey
      });
    }
  });

  return options;
}

/**
 * Returns platform options filtered by selected provider.
 * @param {string} providerValue - Provider key (e.g., 'AWS')
 * @returns {Array<{ label: string, value: string }>}
 */
export function getPlatformsForProviderSync(providerValue) {
  if (!providerValue) return [];

  const providers = resolveProvidersSync();
  const normalizedProvider = providerValue.toLowerCase();
  const seen = new Set();
  const options = [];

  providers.forEach((p) => {
    const pProvider = (p.provider || '').toLowerCase();
    const pPlatform = p.platform || '';

    if (pProvider === normalizedProvider && pPlatform && !seen.has(pPlatform)) {
      seen.add(pPlatform);
      options.push({
        label: p.name || pPlatform,
        value: pPlatform
      });
    }
  });

  return options;
}

/**
 * Returns asset type options filtered by provider and platform.
 * @param {string} providerValue - Provider key (e.g., 'AWS')
 * @param {string} platformValue - Platform key (e.g., 'bedrock_agents')
 * @returns {Array<{ label: string, value: string }>}
 */
export function getAssetTypesForPlatformSync(providerValue, platformValue) {
  if (!providerValue || !platformValue) return [];

  const providers = resolveProvidersSync();
  const normalizedProvider = providerValue.toLowerCase();
  const seen = new Set();
  const options = [];

  providers.forEach((p) => {
    const pProvider = (p.provider || '').toLowerCase();
    const pPlatform = p.platform || '';
    const pAssetType = p.assetType || '';

    const normalizedAssetType = pAssetType.toLowerCase();

    if (
      pProvider === normalizedProvider &&
      pPlatform === platformValue &&
      pAssetType &&
      !seen.has(normalizedAssetType)
    ) {
      seen.add(normalizedAssetType);
      options.push({
        label:
          FORM_ASSET_TYPE_LABELS[normalizedAssetType] ||
          ASSET_TYPE_LABELS[normalizedAssetType] ||
          pAssetType,
        value: normalizedAssetType
      });
    }
  });

  return options;
}

/**
 * Resolves a targetSystem UUID from the (provider, platform, assetType) triple.
 * @param {string} providerValue
 * @param {string} platformValue
 * @param {string} assetTypeValue
 * @returns {string|undefined} targetSystem UUID
 */
export function resolveTargetSystemId(
  providerValue,
  platformValue,
  assetTypeValue
) {
  if (!providerValue || !platformValue || !assetTypeValue) return undefined;

  const providers = resolveProvidersSync();
  const normalizedProvider = providerValue.toUpperCase();

  const match = providers.find((p) => {
    const pProvider = (p.provider || '').toUpperCase();

    return (
      pProvider === normalizedProvider &&
      p.platform === platformValue &&
      (p.assetType || '').toLowerCase() === assetTypeValue.toLowerCase()
    );
  });

  return match?.id;
}

/**
 * Reverse-maps a targetSystem UUID to its (provider, platform, assetType) triple.
 * Used in edit mode to populate the triple selector from an existing scanner.
 * @param {string} targetSystemId - UUID
 * @returns {{ provider: string, platform: string, assetType: string }|null}
 */
export function getTripleFromTargetSystemId(targetSystemId) {
  if (!targetSystemId) return null;

  const providers = resolveProvidersSync();
  const match = providers.find((p) => p.id === targetSystemId);

  if (!match) return null;

  return {
    provider: (match.provider || '').toUpperCase(),
    platform: match.platform || '',
    assetType: match.assetType || ''
  };
}
